import { Component, computed, inject, Injectable, OnInit } from '@angular/core';
import {
    FilterChip,
    Page,
    PageResult,
    QuerySortDirection,
    ReactiveSearchHomeComponent,
    SearchService,
    ServiceHandler,
} from '@nucleus/ui-base';
import { TaskDefinition } from '../../api/runner-service';
import { TaskDefinitionControllerService } from '../../api/runner-service/api/taskDefinitionController.service';

/**
 * Filter envelope for the Task Catalog. {@code value} is the omnibar
 * free-text (matches taskType / name / description / capability / category);
 * {@code capabilities} is a multi-select chip narrow-down so an operator can
 * scope the grid to e.g. "telecom" or "audit" tasks at a glance.
 */
export class TaskCatalogFilter {
    constructor(
        public value: string = '',
        public tags: string[] = [],
    ) {}
}

/**
 * Client-side adapter around the OpenAPI-generated
 * {@link TaskDefinitionControllerService}. tasksense-runner-service exposes
 * only {@code GET /task-definitions} (no server-side filter / pagination),
 * so this adapter fetches the full catalog per refresh and applies
 * filter + sort + page locally — same pattern as the runners admin grid
 * built under TASK-198. Catalog is small (one row per registered
 * (taskType, version)) so the client-side pass is cheap.
 */
@Injectable({ providedIn: 'root' })
export class TaskCatalogSearchAdapter
    implements SearchService<TaskDefinition, TaskCatalogFilter> {

    private readonly api: TaskDefinitionControllerService = inject(TaskDefinitionControllerService);

    search(handler: ServiceHandler<TaskDefinition[]>): void {
        handler.onStart?.();
        this.api.findAll().subscribe({
            next:     rows => handler.onSuccess(rows ?? []),
            error:    err  => handler.onError?.(err),
            complete: ()   => handler.onComplete?.(),
        });
    }

    filter(handler: ServiceHandler<PageResult<TaskDefinition>>,
           filter: TaskCatalogFilter, page?: Page): void {
        handler.onStart?.();
        this.api.findAll().subscribe({
            next: rows => {
                const sortField = page?.sortField ?? 'taskType';
                const sortAsc   = (page?.sortDirection ?? QuerySortDirection.ASC) === QuerySortDirection.ASC;
                const matched   = (rows ?? [])
                    .filter(t => this.matches(t, filter))
                    .sort((a, b) => this.compare(a, b, sortField, sortAsc));
                const total = matched.length;
                const size  = page?.pageSize ?? (total || 1);
                const start = (page?.pageNumber ?? 0) * size;
                const slice = page ? matched.slice(start, start + size) : matched;
                handler.onSuccess(new PageResult<TaskDefinition>(slice, total, page?.pageNumber ?? 0));
            },
            error:    err => handler.onError?.(err),
            complete: ()  => handler.onComplete?.(),
        });
    }

    private matches(t: TaskDefinition, f: TaskCatalogFilter): boolean {
        if (f.tags?.length) {
            const have = new Set((t.tags ?? []).map(c => c.toLowerCase()));
            const need = f.tags.map(c => c.toLowerCase());
            // AND-match: all requested capabilities must be present on the task.
            if (!need.every(c => have.has(c))) return false;
        }
        const search = (f.value ?? '').trim().toLowerCase();
        if (!search) return true;
        const haystack = [
            t.taskType, t.definitionVersion, t.name, t.description,
            (t.tags ?? []).join(' '),
            t.uiHints?.category,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(search);
    }

    private compare(a: TaskDefinition, b: TaskDefinition, field: string, asc: boolean): number {
        const av = (a as any)[field] ?? '';
        const bv = (b as any)[field] ?? '';
        const cmp = String(av).localeCompare(String(bv));
        return asc ? cmp : -cmp;
    }
}

/**
 * Functional view of registered task types — the front door for the
 * schedule-creation wizard. Operators browse tasks here by capability or
 * category, see what each task needs (input schema, connectors, services),
 * and from this page launch the wizard for the selected task.
 *
 * <p>Backed by tasksense-runner-service's task-definition catalog
 * (populated by the Kafka {@code tasksense.task.definition} consumer
 * registered under TASK-159). Each row is one {@code (taskType, version)}
 * combination registered by at least one running runner.
 *
 * <p>Migrated to {@code <lib-list-page>} chrome + the
 * {@link ReactiveSearchHomeComponent} reactive pattern, same shape as the
 * Administration → Runners page (TASK-165 / TASK-198 recipe).
 *
 * <p><b>Deferred (separate ticket):</b> the 4-tab detail dialog
 * (Overview / Input Schema / Connectors+Services / Execution Policy) the
 * TASK-166 spec calls for. This pass builds the list + filtering +
 * permission gate + schedule-creation launch; the full detail dialog is
 * a follow-up.
 */
@Component({
    selector: 'app-tasks',
    templateUrl: './tasks.component.html',
    styleUrl: './tasks.component.css',
    standalone: false,
})
export class TasksComponent
    extends ReactiveSearchHomeComponent<TaskDefinition, TaskCatalogFilter>
    implements OnInit {

    displayedColumns = ['taskType', 'name', 'capabilities', 'category', 'lastRegisteredAt', 'star'];

    /** Capability picker source — derived from whatever capabilities the
     *  currently-loaded tasks declare. Recomputed off {@code dataSource}
     *  signal so it stays in sync with the live catalog. */
    readonly capabilityList = computed<string[]>(() => {
        const rows = this.dataSource() ?? [];
        const set = new Set<string>();
        for (const t of rows) {
            for (const c of (t.tags ?? [])) set.add(c);
        }
        return Array.from(set).sort();
    });

    constructor() {
        super(inject(TaskCatalogSearchAdapter), 'taskType', QuerySortDirection.ASC);
    }

    // ── ReactiveSearchHomeComponent overrides ───────────────────────────────

    override createFilter(): TaskCatalogFilter {
        return new TaskCatalogFilter();
    }

    override onResetFilter(): void {
        this.filter.update(f => Object.assign(f, new TaskCatalogFilter()));
    }

    override isReadyForSearch(): boolean {
        return this.permissionChecker.hasAccessToViewSechedulingTasks();
    }

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToViewSechedulingTasks()) {
            this.showAccessDeny();
            return;
        }
    }

    // ── filter chip rendering ────────────────────────────────────────────────

    activeFilterChips = computed<FilterChip[]>(() => {
        const f = this.filter();
        const chips: FilterChip[] = [];
        for (const cap of (f.tags ?? [])) {
            chips.push({ key: `capability:${cap}`, icon: 'category', label: cap, removable: true });
        }
        return chips;
    });

    removeFilterChip(key: string): void {
        if (key.startsWith('capability:')) {
            const cap = key.substring('capability:'.length);
            this.filter.update(f =>
                Object.assign(Object.create(Object.getPrototypeOf(f)), f, {
                    capabilities: (f.tags ?? []).filter(c => c !== cap),
                }));
        }
    }

    // ── row + cell helpers ──────────────────────────────────────────────────

    /** Category derived from the uiHints.category field (falls back to em-dash). */
    category(t: TaskDefinition): string {
        return t.uiHints?.category ?? '—';
    }

    /** Connector + service requirement count for the detail-cell hint. */
    requirementSummary(t: TaskDefinition): string {
        const conn = Object.keys(t.connectors ?? {}).length;
        const svc  = Object.keys(t.services   ?? {}).length;
        const parts: string[] = [];
        if (conn > 0) parts.push(`${conn} connector${conn === 1 ? '' : 's'}`);
        if (svc  > 0) parts.push(`${svc} service${svc === 1 ? '' : 's'}`);
        return parts.length ? parts.join(' · ') : 'no requirements';
    }

    /** Click-through to the schedule-creation flow pre-populated for this task.
     *  TASK-167 will wire the actual wizard flow off the query params; for now
     *  this navigates to /scheduling/schedule carrying the task identity. */
    onScheduleTask(t: TaskDefinition): void {
        if (!this.permissionChecker.hasAccessToViewSchedulingSchedules()) { this.showAccessDeny(); return; }
        if (!t.taskType || !t.definitionVersion) return;
        this.router.navigate(['/scheduling/schedule'], {
            queryParams: { taskType: t.taskType, definitionVersion: t.definitionVersion },
        });
    }
}
